import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from './ui/dialog';
import {
  CalendarDays, MapPin, Users, Store, Medal, Crown, Loader2, CheckCircle2,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ocToken = () => localStorage.getItem('oc_token') || '';

const TIER_STYLES = {
  platinum: 'border-slate-300/50 bg-slate-300/10 text-slate-200',
  gold: 'border-amber-400/50 bg-amber-400/10 text-amber-400',
  silver: 'border-slate-400/40 bg-slate-400/10 text-slate-400',
  bronze: 'border-orange-700/50 bg-orange-700/10 text-orange-500',
};

function ApplyDialog({ trigger, title, fields, endpoint, testId }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const selectDefaults = Object.fromEntries(
        fields.filter((f) => f.type === 'select').map((f) => [f.key, f.options[0]]));
      const r = await axios.post(`${API}${endpoint}`, { ...selectDefaults, ...form });
      toast.success(r.data.message || 'Application received!');
      setOpen(false);
      setForm({});
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not submit');
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-md" data-testid={testId}>
        <DialogHeader><DialogTitle className="font-heading">{title}</DialogTitle></DialogHeader>
        <div className="space-y-2.5">
          {fields.map((f) => f.type === 'textarea' ? (
            <Textarea key={f.key} rows={3} placeholder={f.label} value={form[f.key] || ''}
              onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} data-testid={`${testId}-${f.key}`} />
          ) : f.type === 'select' ? (
            <select key={f.key} className="w-full h-10 px-3 border rounded-md bg-background text-sm"
              value={form[f.key] || f.options[0]} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
              data-testid={`${testId}-${f.key}`}>
              {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          ) : (
            <Input key={f.key} placeholder={f.label} value={form[f.key] || ''}
              onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} data-testid={`${testId}-${f.key}`} />
          ))}
          <Button onClick={submit} disabled={busy} className="w-full rounded-full bg-orange-500 hover:bg-orange-400 text-white hover:text-white" data-testid={`${testId}-submit`}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Submit application'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export const OctoberCommunity = ({ hasPledge, onNeedPledge }) => {
  const [events, setEvents] = useState([]);
  const [captains, setCaptains] = useState([]);
  const [partners, setPartners] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [partnerFilter, setPartnerFilter] = useState('');

  const load = useCallback(() => {
    axios.get(`${API}/october/events`, { params: { token: ocToken() } }).then((r) => setEvents(r.data.events)).catch(() => {});
    axios.get(`${API}/october/captains`).then((r) => setCaptains(r.data.captains)).catch(() => {});
    axios.get(`${API}/october/sponsors`).then((r) => setSponsors(r.data.sponsors)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    axios.get(`${API}/october/partners`, { params: partnerFilter ? { category: partnerFilter } : {} })
      .then((r) => setPartners(r.data.partners)).catch(() => {});
  }, [partnerFilter]);

  const rsvp = async (id) => {
    if (!ocToken()) { onNeedPledge?.(); return; }
    try {
      const r = await axios.post(`${API}/october/events/${id}/rsvp`, { token: ocToken() });
      toast.success(r.data.going ? "You're in! 🗓️" : 'RSVP removed');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'RSVP failed');
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 space-y-12">
      {/* Kickoff runs */}
      <section id="kickoff-runs" className="scroll-mt-24" data-testid="oc-events-section">
        <h2 className="text-lg font-heading font-bold flex items-center gap-2 mb-4"><CalendarDays className="h-5 w-5 text-orange-400" />Kickoff runs & meetups</h2>
        {events.length === 0 && <p className="text-sm text-muted-foreground">Group runs are being scheduled — check back soon.</p>}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {events.map((e) => (
            <Card key={e.oc_event_id} className="card-lift" data-testid={`oc-event-${e.oc_event_id}`}>
              <CardContent className="p-5 space-y-2.5">
                <div className="flex items-start justify-between gap-2">
                  <p className="font-heading font-semibold">{e.name}</p>
                  <Badge variant="outline" className="shrink-0 text-[10px]">{e.rsvp_count} going</Badge>
                </div>
                <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                  <CalendarDays className="h-3.5 w-3.5" />
                  {new Date(`${e.date}T00:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} · {e.time}
                </p>
                <p className="text-xs text-muted-foreground flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />{e.location}</p>
                {e.description && <p className="text-sm text-muted-foreground leading-snug">{e.description}</p>}
                <Button size="sm" onClick={() => rsvp(e.oc_event_id)}
                  className={`rounded-full ${e.i_am_going ? 'bg-emerald-600 hover:bg-emerald-500' : 'bg-orange-500 hover:bg-orange-400'} text-white hover:text-white`}
                  data-testid={`rsvp-btn-${e.oc_event_id}`}>
                  {e.i_am_going ? <><CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />Going</> : 'RSVP'}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Captains */}
      <section data-testid="oc-captains-section">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <h2 className="text-lg font-heading font-bold flex items-center gap-2"><Medal className="h-5 w-5 text-indigo-400" />Run Captains</h2>
          <ApplyDialog
            testId="captain-apply-dialog"
            title="Become a Run Captain"
            endpoint="/october/captains/apply"
            fields={[
              { key: 'name', label: 'Your name' },
              { key: 'email', label: 'Email' },
              { key: 'location', label: 'Neighborhood / area (e.g. RiNo, Denver)' },
              { key: 'experience', label: 'Tell us about your running / community experience', type: 'textarea' },
            ]}
            trigger={<Button size="sm" variant="outline" className="rounded-full" data-testid="captain-apply-btn">Become a captain</Button>}
          />
        </div>
        {captains.length === 0 && <p className="text-sm text-muted-foreground">Captain crew is forming — apply to lead runs in your neighborhood.</p>}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {captains.map((c) => (
            <Card key={c.captain_id}>
              <CardContent className="p-5 space-y-1.5">
                <p className="font-heading font-semibold">{c.name}</p>
                <p className="text-xs text-muted-foreground flex items-center gap-1.5"><MapPin className="h-3 w-3" />{c.location}</p>
                {c.experience && <p className="text-xs text-muted-foreground leading-snug line-clamp-3">{c.experience}</p>}
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Partners */}
      <section data-testid="oc-partners-section">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <h2 className="text-lg font-heading font-bold flex items-center gap-2"><Store className="h-5 w-5 text-emerald-400" />Community Partners</h2>
          <div className="flex items-center gap-2">
            {['', 'Food', 'Retail', 'Services'].map((c) => (
              <button key={c || 'all'} onClick={() => setPartnerFilter(c)}
                className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${partnerFilter === c ? 'border-emerald-500 bg-emerald-500/10 text-emerald-400' : 'border-border hover:bg-accent'}`}
                data-testid={`partner-filter-${c || 'all'}`}>{c || 'All'}</button>
            ))}
            <ApplyDialog
              testId="partner-apply-dialog"
              title="Become a Community Partner"
              endpoint="/october/partners/apply"
              fields={[
                { key: 'business_name', label: 'Business name' },
                { key: 'email', label: 'Contact email' },
                { key: 'category', label: 'Category', type: 'select', options: ['Food', 'Retail', 'Services'] },
                { key: 'offer', label: 'Your offer for challengers (e.g. 10% off recovery smoothies)', type: 'textarea' },
                { key: 'location', label: 'Location' },
                { key: 'website_url', label: 'Website (optional)' },
              ]}
              trigger={<Button size="sm" variant="outline" className="rounded-full" data-testid="partner-apply-btn">Partner with us</Button>}
            />
          </div>
        </div>
        {partners.length === 0 && <p className="text-sm text-muted-foreground">Local businesses supporting challengers will appear here.</p>}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {partners.map((p) => (
            <Card key={p.partner_id}>
              <CardContent className="p-5 space-y-1.5">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-heading font-semibold">{p.business_name}</p>
                  <Badge variant="outline" className="text-[10px]">{p.category}</Badge>
                </div>
                {p.offer && <p className="text-sm text-emerald-500">{p.offer}</p>}
                <p className="text-xs text-muted-foreground flex items-center gap-1.5"><MapPin className="h-3 w-3" />{p.location}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Sponsors */}
      <section data-testid="oc-sponsors-section">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <h2 className="text-lg font-heading font-bold flex items-center gap-2"><Crown className="h-5 w-5 text-amber-400" />Sponsors</h2>
          <ApplyDialog
            testId="sponsor-apply-dialog"
            title="Sponsor the October Challenge"
            endpoint="/october/sponsors/apply"
            fields={[
              { key: 'company_name', label: 'Company name' },
              { key: 'email', label: 'Contact email' },
              { key: 'tier', label: 'Tier', type: 'select', options: ['platinum', 'gold', 'silver', 'bronze'] },
              { key: 'pledged_amount', label: 'Pledged amount ($)' },
              { key: 'website_url', label: 'Website (optional)' },
            ]}
            trigger={<Button size="sm" variant="outline" className="rounded-full" data-testid="sponsor-apply-btn">Become a sponsor</Button>}
          />
        </div>
        {sponsors.length === 0 && <p className="text-sm text-muted-foreground">Sponsorship tiers: Platinum · Gold · Silver · Bronze. Your logo here.</p>}
        <div className="flex flex-wrap gap-3">
          {sponsors.map((s) => (
            <div key={s.sponsor_id} className={`rounded-xl border px-5 py-3 ${TIER_STYLES[s.tier] || TIER_STYLES.bronze}`}>
              <p className="font-heading font-bold">{s.company_name}</p>
              <p className="text-[10px] uppercase tracking-[0.2em]">{s.tier}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default OctoberCommunity;
